import { gql } from '@apollo/client';

/**
 * Pre-computed PandaDoc analytics — KPIs and chart series for the dashboard.
 * The backend syncs documents hourly and aggregates everything; the frontend
 * just renders the response and re-queries when a filter changes.
 */
export const PandaDocAnalytics = gql`
  query PandaDocAnalytics(
    $granularity: String
    $course: String
    $docType: String
    $dateFrom: Date
    $dateTo: Date
  ) {
    pandadocAnalytics(
      granularity: $granularity
      course: $course
      docType: $docType
      dateFrom: $dateFrom
      dateTo: $dateTo
    ) {
      granularity
      course
      window {
        docsLoaded
        totalInDb
        signedCount
        minDate
        maxDate
      }
      volumeKpi {
        total
        students
        trainers
        dofin
        last30
      }
      ttsKpi {
        medianHours
        meanHours
        pct24h
        pct3d
        sample
      }
      conversionKpi {
        sent
        signed
        active
        lost
        signedPct
        activePct
        lostPct
        ratePct
      }
      volumeSeries {
        students {
          labels
          values
        }
        trainers {
          labels
          values
        }
        dofin {
          labels
          values
        }
      }
      ttsHistogram {
        labels
        values
      }
      ttsByCourse {
        course
        avgDays
        sample
      }
      ttsMedianSeries {
        students {
          labels
          values
        }
        trainers {
          labels
          values
        }
        dofin {
          labels
          values
        }
      }
      conversionByCourse {
        course
        signed
        active
        lost
      }
      conversionSeries {
        students {
          labels
          signed
          active
          lost
        }
        dofin {
          labels
          signed
          active
          lost
        }
      }
    }
  }
`;

/** Relay connection backing the documents table at the bottom of the page. */
export const PandaDocDocuments = gql`
  query PandaDocDocuments(
    $course: String
    $docType: String
    $outcome: String
    $search: String
    $dateFrom: Date
    $dateTo: Date
    $studentId: ID
    $agreementId: ID
    $groupId: ID
    $studentMatches: Boolean
    $first: Int
    $after: String
  ) {
    pandadocDocuments(
      course: $course
      docType: $docType
      outcome: $outcome
      search: $search
      dateFrom: $dateFrom
      dateTo: $dateTo
      studentId: $studentId
      agreementId: $agreementId
      groupId: $groupId
      studentMatches: $studentMatches
      first: $first
      after: $after
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          pk
          pandadocId
          name
          status
          docType
          course
          outcome
          ttsHours
          dateCreated
          dateModified
          dateSent
          dateCompleted
          provisionedAt
          matchType
          folderName
          recipientEmail
          recipientPhone
          agreement {
            id
            agreementTitle
            student {
              id
              user {
                id
                firstName
                lastName
                email
              }
            }
            group {
              id
              name
            }
          }
        }
      }
    }
  }
`;

/**
 * Teacher-scoped PandaDoc documents (spec 0007) — the teacher analog of the
 * student `pandadocDocuments(studentId, studentMatches)`. `teacherMatches:true`
 * additionally surfaces `candidate` documents matched to the teacher by
 * email/phone so a manager can attach them from the teacher card. Operation
 * name ≠ backend field (`pandadocDocuments`) to avoid a codegen collision.
 */
export const TeacherPandaDocDocuments = gql`
  query TeacherPandadocDocs(
    $teacherId: ID
    $teacherMatches: Boolean
    $first: Int
    $after: String
  ) {
    pandadocDocuments(
      teacherId: $teacherId
      teacherMatches: $teacherMatches
      first: $first
      after: $after
    ) {
      totalCount
      pageInfo {
        hasNextPage
        endCursor
      }
      edges {
        cursor
        node {
          id
          pk
          pandadocId
          name
          status
          outcome
          dateCreated
          dateCompleted
          provisionedAt
          matchType
          recipientEmail
          recipientPhone
          contractUrl
          teacher {
            id
          }
        }
      }
    }
  }
`;

/**
 * Folders of signed student contracts in PandaDoc, ready for provisioning.
 * Folder name == expected group name; the backend syncs PandaDoc hourly.
 */
export const PandaDocProvisioningFolders = gql`
  query PandaDocProvisioningFolders($course: String) {
    pandadocProvisioningFolders(course: $course) {
      folderUuid
      folderName
      detectedCourseId
      detectedCourseName
      existingGroupId
      existingGroupName
      studentDocs {
        pandadocId
        pk
        name
        status
        outcome
        firstName
        lastName
        email
        phone
        contractUrl
        phoneMissing
        userExists
        studentExists
        existingUserId
        alreadyProvisioned
      }
    }
  }
`;

/** Sync health — lets the UI warn when conversion data is still incomplete. */
export const PandaDocSyncStatus = gql`
  query PandaDocSyncStatus {
    pandadocSyncStatus {
      documentsCount
      detailsPending
      lastListSyncAt
      lastDetailsSyncAt
      newestDocDate
      lastDocumentsPulled
      lastRunStatus
      lastError
    }
  }
`;
